import { FeatureCollection, Point } from 'geojson'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Layer, Source } from 'react-map-gl'
import { useSelector } from 'react-redux'
import { selectRobot } from 'store/selectors/robot'
import {
  hasRobotMapChassisGeometry,
  iconLayoutLatitude,
  robotIconOffsetPx,
  robotIconSizeAtFineZoom,
  robotIconSizeExpression,
  rotationHandleDistancePx,
} from 'utils/robotIconLayout'

import {
  createRobotPointData,
  getRobotIconName,
  loadAllRobotImages,
  loadRotationHandleImage,
  registerRobotStyleImageMissingHandler,
  setMapCursor,
} from './robotMapUtils'

// Style for rotation handle marker - use arrow instead of circle
const layerRotationHandleStyle: any = {
  id: 'rotation-handle-style',
  type: 'symbol',
  layout: {
    'icon-image': 'rotation-handle',
    'icon-anchor': 'center',
    'icon-rotation-alignment': 'map',
    'icon-size': 1,
    'icon-allow-overlap': true,
    // Rotate arrow so it points outward from robot
    // Arrow is drawn pointing up (north, 0°), so rotate by yaw
    'icon-rotate': ['get', 'yaw'],
  },
  paint: {
    'icon-opacity': 1,
  },
}

interface RobotOnMapEditableProps {
  map: mapboxgl.Map | undefined
  position?: Point
  yaw?: number
  onPositionChanged?: (point: Point) => void
  onYawChanged?: (yaw: number) => void
}

export default function RobotOnMapEditable({
  map,
  position,
  yaw = 0,
  onPositionChanged,
  onYawChanged,
}: RobotOnMapEditableProps) {
  const robot = useSelector(selectRobot)
  const [isDragging, setIsDragging] = useState(false)
  const [isRotating, setIsRotating] = useState(false)
  const [mapUpdateTrigger, setMapUpdateTrigger] = useState(0)
  const dragStartRef = useRef<{ lng: number; lat: number } | null>(null)
  const dragOriginRef = useRef<{
    mouseLng: number
    mouseLat: number
    robotLng: number
    robotLat: number
  } | null>(null)
  const suppressClickAfterDragRef = useRef(false)
  const rotationStartRef = useRef<number>(0)

  const iconLatitude = iconLayoutLatitude(position?.coordinates?.[1])
  const layerRobotStyle = useMemo<any>(() => {
    const lengthM = robot.robotInfo?.lengthM
    const originToFrontM = robot.robotInfo?.originToFrontM
    const hasChassis = hasRobotMapChassisGeometry(lengthM, originToFrontM)
    return {
      id: 'robot-editable-image-style',
      type: 'symbol' as const,
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-anchor': 'center' as const,
        'icon-rotation-alignment': 'map' as const,
        'icon-size': robotIconSizeExpression(robotIconSizeAtFineZoom(hasChassis, iconLatitude)),
        'icon-offset': ['literal', robotIconOffsetPx(originToFrontM)],
        'icon-allow-overlap': true,
        'icon-rotate': ['get', 'yaw'],
      },
      paint: {
        'icon-opacity': 1,
      },
    }
  }, [robot.robotInfo?.lengthM, robot.robotInfo?.originToFrontM, iconLatitude])

  const iconName = useMemo(() => {
    return getRobotIconName(robot.robotInfo?.vendor, robot.robotInfo?.robot_color, robot.robotInfo?.robot_type)
  }, [robot.robotInfo?.vendor, robot.robotInfo?.robot_color, robot.robotInfo?.robot_type])

  const robotPointData = useMemo(() => createRobotPointData(position, yaw, iconName), [position, yaw, iconName])

  // Track map changes to recalculate handle position
  useEffect(() => {
    if (!map) return

    const triggerUpdate = () => {
      setMapUpdateTrigger(prev => prev + 1)
    }

    // Update on any map movement for instant response
    map.on('move', triggerUpdate)
    map.on('zoom', triggerUpdate)
    map.on('rotate', triggerUpdate)

    return () => {
      map.off('move', triggerUpdate)
      map.off('zoom', triggerUpdate)
      map.off('rotate', triggerUpdate)
    }
  }, [map])

  // Calculate rotation handle position directly in useMemo - no state, no delays
  const rotationHandlePosition = useMemo(() => {
    if (!position || !map) {
      return null
    }

    // Don't check map.loaded() - it can be false during updates
    // Instead, just try to calculate, and if error - return null

    const [lng, lat] = position.coordinates

    try {
      // Pose pin on the map (not the PNG center after icon-offset).
      const robotPoint = map.project([lng, lat])
      if (!robotPoint || !isFinite(robotPoint.x) || !isFinite(robotPoint.y)) {
        return null
      }

      // yaw = 0 means north (up), 90° = east (right)
      // In pixels: north (yaw=0) = dy<0 (up), east (yaw=90) = dx>0 (right)
      // But in screen coordinate system: y increases downward, x rightward
      // Therefore: north (yaw=0) = dy<0, east (yaw=90) = dx>0
      const yawRad = (yaw * Math.PI) / 180

      // Past the nose of the drawn sprite, not mid-body over the pose.
      const handleDistancePx = rotationHandleDistancePx({
        zoom: map.getZoom(),
        latitudeDeg: lat,
        lengthM: robot.robotInfo?.lengthM,
        originToFrontM: robot.robotInfo?.originToFrontM,
      })
      const handleX = robotPoint.x + handleDistancePx * Math.sin(yawRad)
      const handleY = robotPoint.y - handleDistancePx * Math.cos(yawRad)

      // Convert back to coordinates
      const handleLngLat = map.unproject([handleX, handleY])

      // Validate coordinates
      if (
        !handleLngLat ||
        !isFinite(handleLngLat.lng) ||
        !isFinite(handleLngLat.lat) ||
        handleLngLat.lat < -90 ||
        handleLngLat.lat > 90 ||
        handleLngLat.lng < -180 ||
        handleLngLat.lng > 180
      ) {
        return null
      }

      return { lng: handleLngLat.lng, lat: handleLngLat.lat }
    } catch (error) {
      // If error occurred during calculation, return null
      return null
    }
  }, [
    position,
    yaw,
    map,
    mapUpdateTrigger,
    isDragging,
    isRotating,
    robot.robotInfo?.lengthM,
    robot.robotInfo?.originToFrontM,
  ])

  // Create GeoJSON data for rotation handle marker
  const rotationHandleData = useMemo<FeatureCollection<Point>>(() => {
    if (!rotationHandlePosition) {
      return {
        type: 'FeatureCollection',
        features: [],
      }
    }
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [rotationHandlePosition.lng, rotationHandlePosition.lat],
          },
          properties: {
            yaw: yaw, // Arrow will be rotated via icon-rotate by yaw angle
          },
        },
      ],
    }
  }, [rotationHandlePosition, yaw])

  useEffect(() => {
    if (!map) return

    loadAllRobotImages(map)
    loadRotationHandleImage(map)

    const onStyleLoad = () => {
      loadAllRobotImages(map)
      loadRotationHandleImage(map)
    }

    map.on('style.load', onStyleLoad)

    const unregisterMissingHandler = registerRobotStyleImageMissingHandler(map)

    return () => {
      map.off('style.load', onStyleLoad)
      unregisterMissingHandler()
    }
  }, [map])

  const onMapClicked = useCallback(
    (e: any) => {
      if (suppressClickAfterDragRef.current) {
        suppressClickAfterDragRef.current = false
        return
      }
      // Ignore clicks during drag
      if (isDragging || isRotating) {
        return
      }
      // Check if clicked on robot or rotation handle
      if (position) {
        const robotPoint = map?.project([position.coordinates[0], position.coordinates[1]])
        const clickPoint = e.point
        if (robotPoint && clickPoint) {
          const distance = Math.sqrt(
            Math.pow(robotPoint.x - clickPoint.x, 2) + Math.pow(robotPoint.y - clickPoint.y, 2)
          )
          // If click is close to robot, don't move
          if (distance < 30) {
            return
          }
        }
      }
      if (rotationHandlePosition) {
        const handlePoint = map?.project([rotationHandlePosition.lng, rotationHandlePosition.lat])
        const clickPoint = e.point
        if (handlePoint && clickPoint) {
          const distance = Math.sqrt(
            Math.pow(handlePoint.x - clickPoint.x, 2) + Math.pow(handlePoint.y - clickPoint.y, 2)
          )
          // If click is close to handle, don't move
          if (distance < 20) {
            return
          }
        }
      }
      // Regular click - move robot
      onPositionChanged && onPositionChanged({ type: 'Point', coordinates: [e.lngLat.lng, e.lngLat.lat] })
    },
    [onPositionChanged, isDragging, isRotating, map, position, rotationHandlePosition]
  )

  const onMouseDown = useCallback(
    (e: any) => {
      if (!position) return

      if (!rotationHandlePosition) return

      // Check if drag started on rotation handle
      const handlePoint = map?.project([rotationHandlePosition.lng, rotationHandlePosition.lat])
      const clickPoint = e.point
      if (handlePoint && clickPoint) {
        const distance = Math.sqrt(
          Math.pow(handlePoint.x - clickPoint.x, 2) + Math.pow(handlePoint.y - clickPoint.y, 2)
        )
        if (distance < 40) {
          setIsRotating(true)
          rotationStartRef.current = yaw
          dragStartRef.current = { lng: e.lngLat.lng, lat: e.lngLat.lat }
          setMapCursor(map, 'grabbing')
          e.preventDefault()
          return
        }
      }

      // Check if drag started on robot
      const robotPoint = map?.project([position.coordinates[0], position.coordinates[1]])
      if (robotPoint && clickPoint) {
        const distance = Math.sqrt(Math.pow(robotPoint.x - clickPoint.x, 2) + Math.pow(robotPoint.y - clickPoint.y, 2))
        if (distance < 40) {
          setIsDragging(true)
          dragStartRef.current = { lng: e.lngLat.lng, lat: e.lngLat.lat }
          dragOriginRef.current = {
            mouseLng: e.lngLat.lng,
            mouseLat: e.lngLat.lat,
            robotLng: position.coordinates[0],
            robotLat: position.coordinates[1],
          }
          setMapCursor(map, 'grabbing')
          e.preventDefault()
        }
      }
    },
    [map, position, rotationHandlePosition, yaw]
  )

  const onMouseMove = useCallback(
    (e: any) => {
      if (!position || !map) return

      if (isRotating && onYawChanged) {
        // Calculate angle between robot center and current cursor position
        const [robotLng, robotLat] = position.coordinates
        const dx = e.lngLat.lng - robotLng
        const dy = e.lngLat.lat - robotLat
        // atan2 returns angle in radians from -π to π
        // atan2(dy, dx) gives angle from X axis (east) counterclockwise
        // On map: dx > 0 = east, dy > 0 = north
        // For icon-rotate in Mapbox: 0° = north (up), 90° = east (right), clockwise
        // Therefore: newYaw = 90 - atan2(dy, dx) in degrees
        let newYaw = (Math.atan2(dy, dx) * 180) / Math.PI
        newYaw = 90 - newYaw
        // Normalize to 0-360 range
        while (newYaw < 0) newYaw += 360
        while (newYaw >= 360) newYaw -= 360
        onYawChanged(newYaw)
        // Handle position will update automatically via useMemo when yaw changes
      } else if (isDragging && dragOriginRef.current && onPositionChanged) {
        const origin = dragOriginRef.current
        onPositionChanged({
          type: 'Point',
          coordinates: [
            origin.robotLng + (e.lngLat.lng - origin.mouseLng),
            origin.robotLat + (e.lngLat.lat - origin.mouseLat),
          ],
        })
      } else if (!isDragging && !isRotating) {
        // Update cursor on hover
        if (rotationHandlePosition) {
          const handlePoint = map?.project([rotationHandlePosition.lng, rotationHandlePosition.lat])
          const mousePoint = e.point
          if (handlePoint && mousePoint) {
            const distance = Math.sqrt(
              Math.pow(handlePoint.x - mousePoint.x, 2) + Math.pow(handlePoint.y - mousePoint.y, 2)
            )
            if (distance < 20) {
              setMapCursor(map, 'grab')
              return
            }
          }
        }
        const robotPoint = map?.project([position.coordinates[0], position.coordinates[1]])
        const mousePoint = e.point
        if (robotPoint && mousePoint) {
          const distance = Math.sqrt(
            Math.pow(robotPoint.x - mousePoint.x, 2) + Math.pow(robotPoint.y - mousePoint.y, 2)
          )
          if (distance < 30) {
            setMapCursor(map, 'move')
            return
          }
        }
        setMapCursor(map, '')
      }
    },
    [isDragging, isRotating, position, map, rotationHandlePosition, onPositionChanged, onYawChanged]
  )

  const onMouseUp = useCallback(() => {
    if (isDragging || isRotating) {
      if (isDragging) suppressClickAfterDragRef.current = true
      setIsDragging(false)
      setIsRotating(false)
      dragStartRef.current = null
      dragOriginRef.current = null
      setMapCursor(map, '')

      // Force marker position update after drag ends
      // Use requestAnimationFrame to ensure update after render
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setMapUpdateTrigger(prev => prev + 1)
        })
      })
    }
  }, [isDragging, isRotating, map])

  useEffect(() => {
    if (!map) return

    map.on('click', onMapClicked)
    map.on('mousedown', onMouseDown)
    map.on('mousemove', onMouseMove)
    map.on('mouseup', onMouseUp)
    map.on('touchstart', onMouseDown)
    map.on('touchmove', onMouseMove)
    map.on('touchend', onMouseUp)

    return () => {
      map.off('click', onMapClicked)
      map.off('mousedown', onMouseDown)
      map.off('mousemove', onMouseMove)
      map.off('mouseup', onMouseUp)
      map.off('touchstart', onMouseDown)
      map.off('touchmove', onMouseMove)
      map.off('touchend', onMouseUp)
    }
  }, [map, onMapClicked, onMouseDown, onMouseMove, onMouseUp])

  if (!position) {
    return null
  }

  const shouldShowMarker = rotationHandlePosition && !isDragging && !isRotating

  return (
    <>
      <Source id="robot-editable" type="geojson" data={robotPointData}>
        <Layer {...layerRobotStyle} />
      </Source>
      {/* Show marker only when not dragging - for better UX */}
      {shouldShowMarker && (
        <Source id="rotation-handle" type="geojson" data={rotationHandleData}>
          <Layer {...layerRotationHandleStyle} />
        </Source>
      )}
    </>
  )
}
